import { useState, useEffect } from 'react';
import { Card } from '../../components/common/Card';
import apiClient from '../../services/api';
import { UsageStats, DailyUsage } from '../../types';

export function UsagePage() {
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [dailyUsage, setDailyUsage] = useState<DailyUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchStats();
  }, [dateRange]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [statsRes, dailyRes] = await Promise.all([
        apiClient.get<UsageStats>(`/usage/stats?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`),
        apiClient.get<DailyUsage[]>(`/usage/daily?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`),
      ]);
      setStats(statsRes.data);
      setDailyUsage(dailyRes.data);
    } catch (error) {
      console.error('Failed to fetch usage stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(2) + 'K';
    return num.toFixed(2);
  };

  const formatCost = (cost: number) => {
    return '$' + cost.toFixed(4);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">用量统计</h1>
        <div className="flex space-x-4">
          <input
            type="date"
            value={dateRange.start_date}
            onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
            className="px-3 py-2 border rounded-lg"
          />
          <input
            type="date"
            value={dateRange.end_date}
            onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
            className="px-3 py-2 border rounded-lg"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <div className="text-center">
                <p className="text-sm text-gray-600">总请求数</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {formatNumber(stats?.total_requests || 0)}
                </p>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <p className="text-sm text-gray-600">总 Token 数</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {formatNumber(stats?.total_tokens || 0)}
                </p>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <p className="text-sm text-gray-600">总费用</p>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {formatCost(stats?.total_cost || 0)}
                </p>
              </div>
            </Card>
          </div>

          <Card title="按模型统计">
            {stats?.by_model && Array.isArray(stats.by_model) && stats.by_model.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">模型</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">请求数</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Token 数</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">费用</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.by_model.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-3 px-4 text-sm">{item.model}</td>
                        <td className="py-3 px-4 text-sm text-right">{formatNumber(item.requests)}</td>
                        <td className="py-3 px-4 text-sm text-right">{formatNumber(item.tokens)}</td>
                        <td className="py-3 px-4 text-sm text-right text-green-600">{formatCost(item.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">暂无数据</p>
            )}
          </Card>

          <Card title="每日用量趋势">
            {dailyUsage && dailyUsage.length > 0 ? (
              <div className="h-64 flex items-end space-x-1">
                {dailyUsage.slice(-30).map((day, index) => {
                  const maxTokens = Math.max(...dailyUsage.map(d => d.tokens), 1);
                  return (
                    <div
                      key={index}
                      className="flex-1 bg-blue-500 rounded-t hover:bg-blue-600 transition-colors"
                      style={{
                        height: `${Math.max(5, (day.tokens / maxTokens) * 100)}%`,
                      }}
                      title={`${day.date}: ${formatNumber(day.tokens)} tokens, ${formatCost(day.cost)}`}
                    />
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">暂无数据</p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
